import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Search, Menu, X } from 'lucide-react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button';
import { Container } from '../ui/Container';
import ThemeToggle from './ThemeToggle';
import SearchBar from './SearchBar';
import LanguageSwitcher from '../ui/LanguageSwitcher';
import AdContainer from '../ads/AdContainer';
import { logoutUser } from '../../store/actions/authActions';
import { useSubscribeModal } from '../../context/SubscribeModalContext';

const HeaderWrapper = styled.header`
  position: sticky;
  top: 0;
  z-index: 50;
  background-color: ${props => props.theme.colors.background}cc;
  backdrop-filter: blur(12px);
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const HeaderContent = styled(Container)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 4rem;
  
  @media (min-width: 768px) {
    height: 5rem;
  }
  
  @media (min-width: 1024px) {
    height: 5.5rem;
  }
  
  @media (min-width: 1440px) {
    height: 6rem;
  }
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  color: inherit;
`;

const LogoBox = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  background-color: ${props => props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius['2xl']};
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (min-width: 1024px) {
    width: 3rem;
    height: 3rem;
  }
  
  @media (min-width: 1440px) {
    width: 3.5rem;
    height: 3.5rem;
  }
`;

const LogoLetter = styled.span`
  color: ${props => props.theme.colors.primaryForeground};
  font-family: ${props => props.theme.typography.fonts.display};
  font-weight: ${props => props.theme.typography.fontWeights.bold};
  font-size: 1rem;
  
  @media (min-width: 640px) {
    font-size: 1.125rem;
  }
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 1.375rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 1.5rem;
  }
  
  @media (min-width: 1920px) {
    font-size: 1.625rem;
  }
`;

const LogoText = styled.span`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 1.125rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  display: none;
  
  @media (min-width: 640px) {
    display: block;
    font-size: 1.25rem;
  }
  
  @media (min-width: 768px) {
    font-size: 1.375rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 1.5rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 1.625rem;
  }
  
  @media (min-width: 1920px) {
    font-size: 1.75rem;
  }
`;

const Nav = styled.nav`
  display: none;
  align-items: center;
  gap: 2rem;
  
  @media (min-width: 768px) {
    display: flex;
  }
`;

const NavLink = styled(Link).attrs(props => ({
  // Filter out the 'active' prop so it doesn't get passed to DOM
  active: undefined
}))`
  font-size: 0.9375rem;
  font-weight: 500;
  color: ${props => props.$active ? props.theme.colors.foreground : props.theme.colors.mutedForeground};
  text-decoration: none;
  transition: color 0.2s;
  
  @media (min-width: 768px) {
    font-size: 1rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 1.0625rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 1.125rem;
  }
  
  @media (min-width: 1920px) {
    font-size: 1.25rem;
  }
  
  &:hover {
    color: ${props => props.$active ? props.theme.colors.mutedForeground : props.theme.colors.foreground};
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SubscribeButton = styled(Button)`
  display: none;
  
  @media (min-width: 640px) {
    display: flex;
  }
`;

const MobileMenuButton = styled(Button)`
  display: flex;
  
  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileMenu = styled.div`
  display: block;
  border-top: 1px solid ${props => props.theme.colors.border};
  background-color: ${props => props.theme.colors.background};
  animation: fadeIn 0.5s ease-out forwards;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileNav = styled.nav`
  ${Container} {
    padding-top: 1rem;
    padding-bottom: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  
  a {
    font-size: 1rem;
    font-weight: 500;
    padding: 0.5rem 0;
    text-decoration: none;
    color: ${props => props.theme.colors.foreground};
    
    @media (min-width: 640px) {
      font-size: 1.0625rem;
    }
    
    @media (min-width: 768px) {
      font-size: 1.125rem;
    }
    
    &:not(:first-child) {
      color: ${props => props.theme.colors.mutedForeground};
    }
  }
  
  ${Button} {
    margin-top: 0.5rem;
  }
`;

const AuthLink = styled(Link)`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }

  @media (max-width: 767px) {
    display: none;
  }
`;

const SignOutBtn = styled.button`
  border: none;
  background: none;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${props => props.theme.colors.mutedForeground};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    color: ${props => props.theme.colors.foreground};
  }

  @media (max-width: 767px) {
    display: none;
  }
`;

const Header = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { openSubscribe } = useSubscribeModal();
  const { isAuthenticated, user } = useSelector((s) => s.auth);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const location = useLocation();

  const handleSubscribeClick = () => {
    setIsMenuOpen(false);
    openSubscribe();
  };

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <HeaderWrapper>
      <HeaderContent>
        <LogoLink to="/">
          <LogoBox>
            <LogoLetter>B</LogoLetter>
          </LogoBox>
          <LogoText>{t('site.name')}</LogoText>
        </LogoLink>

        <Nav>
          <NavLink to="/" $active={isActive('/')}>{t('nav.home')}</NavLink>
          <NavLink to="/categories" $active={isActive('/categories')}>{t('nav.categories')}</NavLink>
          <NavLink to="/trending" $active={isActive('/trending')}>{t('nav.trending')}</NavLink>
          <NavLink to="/about" $active={isActive('/about')}>{t('footer.about')}</NavLink>
        </Nav>

        <Actions>
          <Button 
            variant="ghost" 
            size="icon" 
            aria-label={t('nav.search')}
            onClick={() => setIsSearchOpen(!isSearchOpen)}
          >
            <Search />
          </Button>
          <SearchBar isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
          <LanguageSwitcher />
          <ThemeToggle />
          {isAuthenticated ? (
            <>
              <AuthLink to="/account">{user?.username || t('auth.account')}</AuthLink>
              <SignOutBtn type="button" onClick={() => dispatch(logoutUser())}>
                {t('auth.signOut')}
              </SignOutBtn>
            </>
          ) : (
            <AuthLink to="/login">{t('auth.signIn')}</AuthLink>
          )}
          <SubscribeButton type="button" onClick={handleSubscribeClick}>
            {t('newsletter.button')}
          </SubscribeButton>
          <MobileMenuButton
            variant="ghost"
            size="icon"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? t('common.close') + ' menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
          >
            {isMenuOpen ? <X /> : <Menu />}
          </MobileMenuButton>
        </Actions>
      </HeaderContent>
      
      {/* Header Ad */}
      <AdContainer placement="header" limit={1} />

      {isMenuOpen && (
        <MobileMenu>
          <MobileNav>
            <Container>
              <Link to="/">{t('nav.home')}</Link>
              <Link to="/categories">{t('nav.categories')}</Link>
              <Link to="/trending">{t('nav.trending')}</Link>
              <Link to="/about">{t('footer.about')}</Link>
              {isAuthenticated ? (
                <>
                  <Link to="/account">{user?.username || t('auth.account')}</Link>
                  <Button variant="ghost" onClick={() => dispatch(logoutUser())}>
                    {t('auth.signOut')}
                  </Button>
                </>
              ) : (
                <Link to="/login">{t('auth.signIn')}</Link>
              )}
              <Button type="button" onClick={handleSubscribeClick}>
                {t('newsletter.button')}
              </Button>
            </Container>
          </MobileNav>
        </MobileMenu>
      )}
    </HeaderWrapper>
  );
};

export default Header;
